'use client';

import { useId, useState } from 'react';
import { useTranslations } from 'next-intl';

import { CONTACT_EMAIL } from '@/lib/constants';

type Status = 'idle' | 'success';

const MAILTO_SUBJECT = 'Sandbox Access Request';
const MAILTO_BODY = (email: string) =>
	`Please grant sandbox access. Reply to: ${email}`;

/**
 * Footer comms panel — ported 1:1 from the ONEKYC design.
 * Underline email field + full-width toggling button + a terminal status line
 * with a blinking cursor. Behaviour is ours: submitting drafts a mailto.
 * Colors are hardcoded to the design's always-dark palette (#f2f2f4 on #0a0a0b)
 * rather than theme tokens, because this footer is a dark island on any theme.
 */
export function EmailForm() {
	const t = useTranslations('EmailForm');
	const emailId = useId();
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<Status>('idle');

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!email) return;
		const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
			MAILTO_SUBJECT,
		)}&body=${encodeURIComponent(MAILTO_BODY(email))}`;
		window.location.assign(href);
		setStatus('success');
		setEmail('');
		setTimeout(() => setStatus('idle'), 3000);
	};

	const sent = status === 'success';

	return (
		<form onSubmit={handleSubmit}>
			<div className="font-mono text-[10.5px] tracking-[2px] text-[#f2f2f4]/50">
				{t('heading')}
			</div>

			<label htmlFor={emailId} className="sr-only">
				{t('emailLabel')}
			</label>
			<input
				id={emailId}
				type="email"
				name="email"
				autoComplete="email"
				inputMode="email"
				required
				placeholder={t('placeholder')}
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				className="mt-4 w-full border-0 border-b border-white/20 bg-transparent py-[11px] font-mono text-base tracking-[0.5px] text-[#f2f2f4] transition-colors outline-none focus:border-[#f2f2f4] motion-reduce:transition-none"
			/>

			<button
				type="submit"
				aria-live="polite"
				className={`mt-[22px] flex w-full cursor-pointer items-center justify-between border px-[18px] py-[15px] font-mono text-xs tracking-[2px] uppercase transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[#f2f2f4] motion-reduce:transition-none ${
					sent
						? 'border-white/40 bg-transparent text-[#f2f2f4]'
						: 'border-[#f2f2f4] bg-[#f2f2f4] text-[#0a0a0b]'
				}`}
			>
				<span>{sent ? t('submitSuccess') : t('submitIdle')}</span>
				<span>{sent ? '✓' : '->'}</span>
			</button>

			<div className="mt-3 font-mono text-[10px] tracking-[1px] text-[#f2f2f4]/50 uppercase">
				&gt; {sent ? t('statusDone') : t('statusIdle')}{' '}
				<span className="onekyc-blink" aria-hidden="true">
					_
				</span>
			</div>
		</form>
	);
}
